import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose, IoShareSocialOutline, IoCheckmark, IoPerson, IoShieldCheckmark, IoTimeOutline, IoDiamond, IoBusiness } from 'react-icons/io5';
import {
    DetailModalOverlay, DetailModalContent, DetailBody,
    TypeBadge
} from '../styles/SpotStyles';
import SPOT_MESSAGES from '../constants/SpotMessages';
import { useAppContext } from '../../../contexts/AppContext';
import { getLocalizedTitle, getLocalizedContent } from '../utils/spotUtils';
import LandmarkActionUI from './LandmarkActionUI'; // 🎯 [Phase 4] Integrated Action UI
import { calculateEstimatedEarnings } from '../hooks/useLandmarkManager'; // 🎯 [v4.1] Earnings calculator

import { useCityEquipped } from '../../../features/city/hooks/useCityEquipped';
import { useCityInventory } from '../../../features/city/hooks/useCityInventory';
import { CITY_KEYS } from '../../../features/city/constants';
import { cityLandmarkBonusMultiplier, sumCityLandmarkSpotBonusPercent } from '../../../features/city/cityBonusUtils';

const LandmarkDetailModal = ({
    detailedSpot,
    piUser,
    t,
    onClose,
    landmarkState,
    onVisit,
    onClaim,
    onConfirmVisit,
    onAdVisit, // 🎯 [Phase 4] Ad Visit Handler
    adVisitLoading = false // 🐛 [Bugfix 2026-07-09] 광고 방문 처리 중 버튼 잠금
}) => {
    const { showAlert, landmarkSpotRate, landmarkOccupancyLimit } = useAppContext();
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const environment = useAppContext().environment || 'sandbox'; // Get environment from context
    const { equippedList } = useCityInventory(piUser?.uid, environment);
    const myEquippedSlots = React.useMemo(
        () => (Array.isArray(equippedList) ? equippedList.filter((x) => x != null) : []),
        [equippedList]
    );
    const bonusMultiplier = cityLandmarkBonusMultiplier(myEquippedSlots);
    const myBonusPercent = sumCityLandmarkSpotBonusPercent(myEquippedSlots);
    const [showShareModal, setShowShareModal] = useState(false);

    // 🎯 [UI/UX] Fetch Owner's Equipped City for Display (다중 슬롯 — CityEquippedCache가 equippedList 반영)
    const ownerId = landmarkState?.status?.ownerId;
    const { equipped: ownerEquipped } = useCityEquipped(ownerId, environment);
    const ownerEquippedSlots = React.useMemo(
        () => (Array.isArray(ownerEquipped) ? ownerEquipped.filter((x) => x != null) : []),
        [ownerEquipped]
    );
    const displayEquippedSlots = React.useMemo(() => {
        if (landmarkState.isEmpty) return [];
        if (landmarkState.isMyLand) return myEquippedSlots;
        return ownerEquippedSlots;
    }, [landmarkState.isEmpty, landmarkState.isMyLand, myEquippedSlots, ownerEquippedSlots]);
    const displayBonusPercent = sumCityLandmarkSpotBonusPercent(displayEquippedSlots);
    const citySlotCount = displayEquippedSlots.length;
    const cityThumbPx = citySlotCount >= 3 ? 40 : citySlotCount === 2 ? 48 : 56;
    const cityThumbGap = citySlotCount >= 3 ? 4 : 6;
    const cityThumbRadius = Math.max(8, Math.round(cityThumbPx * 0.22));

    // 🎯 [v4.1] Real-time earnings update
    const [currentTime, setCurrentTime] = useState(Date.now());

    // 🎯 [Drag Handlers] Self-managed drag state
    const [sheetY, setSheetY] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const startYRef = useRef(0);

    const handleTouchStart = (e) => {
        startYRef.current = e.touches[0].clientY;
        setIsAnimating(false);
    };

    const handleTouchMove = (e) => {
        const currentY = e.touches[0].clientY;
        const diff = currentY - startYRef.current;
        if (diff > 0) { setSheetY(diff); }
    };

    const handleTouchEnd = (e) => {
        const diff = e.changedTouches[0].clientY - startYRef.current;
        setIsAnimating(true);
        if (diff > 120) { onClose(); } else { setSheetY(0); }
    };

    // 🎯 [v4.1] Update earnings every second for owned landmarks
    // 🎯 [Auto-Settlement Fix] Also check for expiration and trigger refresh
    useEffect(() => {
        if (landmarkState.isMyLand && landmarkState.status?.capturedAt) {
            const interval = setInterval(() => {
                const now = Date.now();
                setCurrentTime(now);

                // 🎯 [Auto-Settlement Fix] Check if expired and trigger refresh
                const expiresAt = landmarkState.status?.expiresAt?.toDate ?
                    landmarkState.status.expiresAt.toDate().getTime() :
                    (landmarkState.status?.expiresAt || 0);

                if (expiresAt > 0 && now >= expiresAt) {
                    // Landmark expired - Firestore listener will automatically update
                    // No server call needed (cost optimization)
                    console.log('[LandmarkDetailModal] Landmark expired, waiting for auto-settlement...');
                }
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [landmarkState.isMyLand, landmarkState.status?.capturedAt, landmarkState.status?.expiresAt]);


    // 🎯 [v4.1] Calculate estimated earnings
    const earningsInfo = React.useMemo(() => {
        if (!landmarkState.isMyLand || !landmarkState.status?.capturedAt) {
            return null;
        }
        const capturedAt = landmarkState.status.capturedAt?.toDate?.() || landmarkState.status.capturedAt;
        const capturedMs = new Date(capturedAt).getTime();
        const elapsedMs = currentTime - capturedMs;
        const elapsedSec = Math.floor(elapsedMs / 1000);
        const elapsedMin = Math.floor(elapsedSec / 60);

        // Dynamic capping based on landmarkOccupancyLimit (hours)
        const limitHours = landmarkOccupancyLimit || 24;
        const maxSeconds = limitHours * 3600;


        const estimatedSpot = calculateEstimatedEarnings(landmarkState.status, landmarkSpotRate, limitHours, new Date(currentTime), bonusMultiplier);

        // 남은 시간
        const remainingSec = Math.max(0, maxSeconds - elapsedSec);

        return {
            estimatedSpot: estimatedSpot.estimatedSpot,
            elapsedMin,
            remainingSec,
            isExpired: elapsedSec >= maxSeconds,
            bonusApplied: estimatedSpot.bonusApplied
        };
    }, [landmarkState.isMyLand, landmarkState.status, currentTime, landmarkSpotRate, landmarkOccupancyLimit, bonusMultiplier]);

    const myLandDescription = React.useMemo(() => {
        if (!landmarkState.isMyLand) return null;
        const remainingSec = Math.max(0, earningsInfo?.remainingSec || 0);
        const totalHours = Math.ceil(remainingSec / 3600);
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;
        if (remainingSec <= 0) return t('my_land_desc');
        return t('my_land_desc_with_remaining', { days, hours });
    }, [landmarkState.isMyLand, earningsInfo?.remainingSec, t]);

    if (!detailedSpot) return null;

    // Helper to get status color
    const getStatusColor = () => {
        if (landmarkState.isEmpty) return '#14B8A6'; // Teal for Free
        if (landmarkState.isMyLand) return '#7C3AED'; // Purple for Mine
        return '#6B7280'; // Gray for Occupied
    };

    const statusColor = getStatusColor();



    return (
        <DetailModalOverlay
            onClick={onClose}
            style={{ alignItems: 'flex-end' }}
        >
            <DetailModalContent
                onClick={(e) => e.stopPropagation()}
                style={{
                    transform: `translateY(${sheetY}px)`,
                    transition: isAnimating ? 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none',
                    height: 'auto',
                    maxHeight: '85vh',
                    minHeight: '450px',
                    borderTopLeftRadius: '24px',
                    borderTopRightRadius: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: 0,
                    background: '#FFFFFF'
                }}
            >
                {/* 1. Drag Handle Area */}
                <div
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{
                        width: '100%', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, cursor: 'grab', touchAction: 'none', background: 'transparent', zIndex: 10
                    }}
                >
                    <div style={{ width: '40px', height: '4px', background: '#E5E7EB', borderRadius: '2px' }} />
                </div>

                {/* 2. Header Actions (Absolute) */}
                <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', gap: '8px', zIndex: 20 }}>
                    <button onClick={() => setShowShareModal(true)} style={{ background: '#F3F4F6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <IoShareSocialOutline size={18} color="#4B5563" />
                    </button>
                    <button onClick={onClose} style={{ background: '#F3F4F6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <IoClose size={20} color="#4B5563" />
                    </button>
                </div>

                {/* 3. Hero Header (Typography - Left Aligned) */}
                <div style={{ padding: '10px 24px 10px', textAlign: 'left', position: 'relative' }}>
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundImage: 'radial-gradient(#E5E7EB 1px, transparent 1px)',
                        backgroundSize: '20px 20px', opacity: 0.3, zIndex: 0
                    }} />

                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <TypeBadge $type="landmark" style={{ margin: '0 0 12px 0', width: 'fit-content' }}>
                            <IoBusiness /> LANDMARK
                        </TypeBadge>
                        <h2 style={{ fontSize: '1.75rem', fontWeight: '900', margin: '0 0 8px 0', color: '#1F2937', letterSpacing: '-0.5px' }}>
                            {getLocalizedTitle(detailedSpot, currentLang)}
                        </h2>
                        <div style={{ fontSize: '0.9rem', color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '6px' }}>
                            {detailedSpot.distance} • {detailedSpot.geo ? `${(detailedSpot.geo.lat || detailedSpot.geo.latitude)?.toFixed(4)}, ${(detailedSpot.geo.lng || detailedSpot.geo.longitude)?.toFixed(4)}` : ''}
                        </div>
                    </div>
                </div>

                <DetailBody style={{ flex: 1, padding: '0 24px 24px', display: 'flex', flexDirection: 'column' }}>

                    {/* 🎯 [Round 3] Expert UI Refinement: Separation of Status & Owner */}

                    {/* 1. Status Bar (The "Space") */}
                    <div style={{
                        width: '100%',
                        background: landmarkState.isEmpty ? '#F0FDFA' : (landmarkState.isMyLand ? '#F5F3FF' : '#F3F4F6'),
                        border: `1px solid ${statusColor}40`,
                        borderRadius: '12px',
                        padding: '10px 16px',
                        marginBottom: '16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {landmarkState.isEmpty
                                ? <IoBusiness color={statusColor} />
                                : (landmarkState.isMyLand ? <IoShieldCheckmark color={statusColor} /> : <IoTimeOutline color={statusColor} />)
                            }
                            <span style={{ fontWeight: '700', color: statusColor, fontSize: '0.95rem' }}>
                                {landmarkState.isEmpty
                                    ? t('landmark_status_vacant')
                                    : (landmarkState.isMyLand
                                        ? (landmarkState.isProtected ? t('landmark_status_protected') : t('landmark_status_occupied'))
                                        : t('landmark_status_occupied')
                                    )
                                }
                            </span>
                        </div>
                        {/* Protection Timer / Expiration info could go here or remain in existing bottom area if detailed */}
                    </div>

                    {/* 2. Owner Identity Card — 닉네임 전체 표시(줄바꿈), CITY는 별도 행으로 배치 */}
                    <div style={{
                        background: 'white',
                        borderRadius: '20px',
                        padding: '20px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '14px',
                        marginBottom: '24px',
                        border: '1px solid rgba(0,0,0,0.05)'
                    }}>
                        <div style={{ fontSize: '0.8rem', color: '#9CA3AF', fontWeight: '600' }}>{t('landmark_current_owner')}</div>

                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '10px', width: '100%', minWidth: 0 }}>
                            <div style={{
                                width: '42px', height: '42px', borderRadius: '50%', flexShrink: 0,
                                background: landmarkState.isEmpty ? '#E5E7EB' : (landmarkState.isMyLand ? '#EDE9FE' : '#F3F4F6'),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: statusColor
                            }}>
                                {landmarkState.isEmpty ? <IoBusiness size={20} /> : <IoPerson size={20} />}
                            </div>
                            <span style={{
                                flex: 1,
                                minWidth: 0,
                                fontWeight: '800',
                                fontSize: '1.1rem',
                                color: '#1F2937',
                                lineHeight: 1.35,
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                {landmarkState.isEmpty
                                    ? t('landmark_vacant')
                                    : (landmarkState.isMyLand
                                        ? (landmarkState.status?.ownerName || piUser?.username || 'Me')
                                        : (landmarkState.status?.ownerName || 'Unknown')
                                    )
                                }
                            </span>
                        </div>

                        {!landmarkState.isEmpty && displayEquippedSlots.length > 0 ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '8px',
                                width: '100%',
                                paddingTop: '4px',
                                borderTop: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                <div style={{
                                    display: 'flex', flexDirection: 'row', gap: `${cityThumbGap}px`, justifyContent: 'flex-end',
                                    flexWrap: 'nowrap',
                                    width: '100%',
                                    maxWidth: '100%',
                                    overflowX: 'auto',
                                    overflowY: 'hidden',
                                    WebkitOverflowScrolling: 'touch',
                                    paddingBottom: '2px',
                                    boxSizing: 'border-box'
                                }}>
                                    {displayEquippedSlots.map((token, idx) => (
                                        <div
                                            key={token.tokenId || token.cityId || idx}
                                            style={{
                                                width: `${cityThumbPx}px`, height: `${cityThumbPx}px`,
                                                borderRadius: `${cityThumbRadius}px`,
                                                overflow: 'hidden',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                flexShrink: 0
                                            }}
                                        >
                                            <img
                                                src={token.thumbnailUrl || token.imageUrl}
                                                alt=""
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {displayBonusPercent > 0 && (
                                    <div style={{
                                        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                        color: 'white',
                                        fontSize: '0.65rem', fontWeight: '800',
                                        padding: '2px 8px', borderRadius: '10px',
                                        whiteSpace: 'nowrap',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        display: 'flex', alignItems: 'center', gap: '2px'
                                    }}>
                                        <IoDiamond size={8} />
                                        <span>+{displayBonusPercent}%</span>
                                    </div>
                                )}
                            </div>
                        ) : !landmarkState.isEmpty ? (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                width: '100%',
                                paddingTop: '4px',
                                borderTop: '1px solid rgba(0,0,0,0.06)'
                            }}>
                                <div style={{
                                    width: '70px', height: '70px',
                                    borderRadius: '14px',
                                    background: '#F9FAFB',
                                    border: '2px dashed #E5E7EB',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    gap: '2px'
                                }}>
                                    <IoBusiness size={18} color="#D1D5DB" />
                                    <span style={{ fontSize: '0.6rem', color: '#9CA3AF' }}>No NFT</span>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* 🎯 [v4.1] Estimated Earnings Card (My Land Only) */}
                    {landmarkState.isMyLand && earningsInfo && (
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '16px',
                            padding: '16px',
                            marginBottom: '16px',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)'
                        }}>
                            <div style={{ fontSize: '0.7rem', opacity: 0.9, marginBottom: '6px', fontWeight: '600' }}>
                                💰 {t('landmark_profit_estimated')}
                            </div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '10px', letterSpacing: '-0.3px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {earningsInfo.estimatedSpot.toFixed(1)} SPOT
                                {earningsInfo.bonusApplied && myBonusPercent > 0 && (
                                    <span style={{ fontSize: '0.65rem', background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: '4px' }}>
                                        +{myBonusPercent}% {t(CITY_KEYS.EQUIP)}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '0.75rem', opacity: 0.95, flexWrap: 'wrap' }}>
                                <div>⏱️ {t('landmark_elapsed')}: {Math.floor(earningsInfo.elapsedMin / 60)}h {earningsInfo.elapsedMin % 60}m</div>
                                <div>⏳ {t('landmark_remaining')}: {Math.floor(earningsInfo.remainingSec / 3600)}h {Math.floor((earningsInfo.remainingSec % 3600) / 60)}m</div>
                            </div>
                        </div>
                    )}

                    {/* 🎯 [v4.1] Auto-Return Notice (All Occupied Landmarks) */}
                    {!landmarkState.isEmpty && landmarkState.status?.capturedAt && (
                        <div style={{
                            background: landmarkState.isMyLand ? '#FEF3C7' : '#F3F4F6',
                            border: `1px solid ${landmarkState.isMyLand ? '#FCD34D' : '#E5E7EB'}`,
                            borderRadius: '10px',
                            padding: '10px 12px',
                            fontSize: '0.75rem',
                            color: landmarkState.isMyLand ? '#78350F' : '#4B5563',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginBottom: '16px',
                            lineHeight: '1.4'
                        }}>
                            <IoTimeOutline size={14} />
                            <span>
                                {landmarkState.isMyLand
                                    ? t('landmark_auto_return_mine', { hours: landmarkOccupancyLimit })
                                    : t('landmark_auto_return_other', { hours: landmarkOccupancyLimit })}
                            </span>
                        </div>
                    )}

                    {/* 5. Description (Minimal) */}
                    <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '16px', marginBottom: 'auto', border: '1px solid #F3F4F6' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <IoDiamond color="#6C5CE7" />
                            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#4B5563' }}>{t('landmark_info')}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5', color: '#6B7280' }}>
                            {myLandDescription || getLocalizedContent(detailedSpot, currentLang) || t('landmark_default_desc')}
                        </p>
                    </div>

                    {/* 6. Integrated Action UI (Full Width Bottom with Safe Area Padding) */}
                    <div style={{
                        marginTop: '24px',
                        paddingBottom: 'calc(40px + env(safe-area-inset-bottom))',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <LandmarkActionUI
                            landmarkId={detailedSpot.id}
                            landmarkState={landmarkState}
                            onVisit={onVisit}
                            onClaim={onClaim}
                            onConfirmVisit={onConfirmVisit}
                            onAdVisit={onAdVisit} // 🎯 [Phase 4] Pass Ad Handler
                            adVisitLoading={adVisitLoading} // 🐛 [Bugfix 2026-07-09] 광고 방문 처리 중 버튼 잠금
                            isModal={true}
                            fullWidth={true} // 🎯 New Prop for full width button
                            isMyLandSlot={detailedSpot?.id?.startsWith?.('myland_')}
                        />
                    </div>
                </DetailBody>
            </DetailModalContent>

            {/* Share Modal (Reused) */}
            {showShareModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1100,
                        padding: '20px'
                    }}
                    onClick={() => setShowShareModal(false)}
                >
                    <div
                        style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '24px',
                            width: '100%',
                            maxWidth: '400px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{
                            margin: '0 0 16px 0',
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            color: '#374151'
                        }}>
                            {t('share')}
                        </h3>

                        {/* URL 표시 - 🎯 [Deep Link] spotId 포함 */}
                        <div style={{
                            background: '#F3F4F6',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '16px',
                            wordBreak: 'break-all',
                            fontSize: '0.85rem',
                            color: '#6B7280',
                            border: '1px solid #E5E7EB'
                        }}>
                            {`${window.location.origin}${window.location.pathname}#/main?spotId=${detailedSpot.id}`}
                        </div>

                        {/* 복사 버튼 */}
                        <button
                            onClick={async () => {
                                // 🎯 [Deep Link] spotId 포함 URL 생성
                                const shareUrl = `${window.location.origin}${window.location.pathname}#/main?spotId=${detailedSpot.id}`;
                                try {
                                    await navigator.clipboard.writeText(shareUrl);
                                    showAlert(t(SPOT_MESSAGES.LINK_COPIED));
                                } catch (err) {
                                    // Legacy fallback
                                    const textArea = document.createElement('textarea');
                                    textArea.value = shareUrl;
                                    textArea.style.position = 'fixed';
                                    textArea.style.opacity = '0';
                                    document.body.appendChild(textArea);
                                    textArea.select();
                                    document.execCommand('copy');
                                    document.body.removeChild(textArea);
                                    showAlert(t(SPOT_MESSAGES.LINK_COPIED));
                                }
                                setShowShareModal(false);
                            }}
                            style={{
                                width: '100%',
                                padding: '14px',
                                background: '#6C5CE7',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '0.95rem',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <IoCheckmark size={18} /> {t('copy_url')}
                        </button>

                        {/* 닫기 버튼 */}
                        <button
                            onClick={() => setShowShareModal(false)}
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: 'transparent',
                                color: '#6B7280',
                                border: 'none',
                                fontSize: '0.9rem',
                                marginTop: '8px',
                                cursor: 'pointer'
                            }}
                        >
                            {t('close')}
                        </button>
                    </div>
                </div>
            )}
        </DetailModalOverlay>
    );
};

export default LandmarkDetailModal;
